import React from 'react';
import _ from 'lodash';

import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';
import {
  Done,
  Error
} from '@mui/icons-material';
import {
  FormModal,
  routeDirectory
} from 'modules';
import {
  fields,
  formUtil
} from 'iron-forge-react-modules';

import { Validator } from 'jsonschema';
import { useNavigate } from 'react-router-dom';
import * as adminShared from '../../../shared/validationErrors';
import * as types from './types';
import * as dataService from './dataService';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



export interface IProps {
  cachedOrderId: string;
  onClose: () => void;
}



export const FC: React.FC<IProps> = props => {
  const navigate = useNavigate();
  const [orderAsString, setOrderAsString] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState<types.IValidationError[]>([]);
  const [didValidateWithServer, setDidValidateWithServer] = React.useState(false);
  const form = useForm<types.IFormState>({
    defaultValues: {
      errors: []
    }
  });
  const fieldArray = useFieldArray<types.IFormState>({
    control: form.control,
    name: 'errors'
  });
  const queryTuple = dataService.useQuery(props.cachedOrderId);
  const [
    validateCachedOrder,
    validateCachedOrderTuple
  ] = dataService.useValidateCachedOrder(props.cachedOrderId);
  const [
    overrideCachedOrder,
    overrideCachedOrderTuple
  ] = dataService.useOverrideCachedOrder(props.cachedOrderId);


  React.useEffect(
    () => {
      const errors = _.filter(validationErrors, err => err.type === 'data');
      formUtil.setFieldsFromQuery(
        { errors },
        form.setValue,
        ['errors']
      );
    },
    [
      form.setValue,
      validationErrors
    ]
  );

  React.useEffect(
    () => {
      setDidValidateWithServer(false);
    },
    [orderAsString]
  );



  React.useEffect(
    () => {
      if (!_.isEmpty(queryTuple.data.originalOrder) && !orderAsString) {
        setOrderAsString(JSON.stringify(queryTuple.data?.originalOrder ?? {}, null, 2));
      }
    },
    [
      orderAsString,
      queryTuple.data.originalOrder
    ]
  );



  React.useEffect(
    () => {
      try {
        const order = JSON.parse(orderAsString);
        const v = new Validator();
        const result = v.validate(order, queryTuple.data.salesOrderSchema, {
          nestedErrors: true
        } as unknown as undefined);
        const flattenedErrors = adminShared.flattenValidationErrors(result.errors);
        setValidationErrors(_.map(flattenedErrors, (err): types.IValidationError => ({
          property: adminShared.getValidationErrorPropertyPath(err),
          message: err.toString(),
          type: 'data',
          newValue: ''
        })));

      } catch (err) {
        setValidationErrors([{
          type: 'parse',
          message: "Invalid JSON",
          property: '',
          newValue: ''
        }]);
      }
    },
    [
      orderAsString,
      queryTuple.data.salesOrderSchema
    ]
  );



  const validateOrder = React.useCallback(
    async (orderAsString: string) => {
      if (!didValidateWithServer && orderAsString) {
        const results = await validateCachedOrder(JSON.parse(orderAsString));
        setValidationErrors(_.map(results, (res): types.IValidationError => ({
          type: 'server',
          message: res,
          property: '',
          newValue: ''
        })));
        setDidValidateWithServer(true);
      }
    },
    [
      didValidateWithServer,
      validateCachedOrder
    ]
  );



  React.useEffect(
    () => {
      if (_.isEmpty(validationErrors) && !validateCachedOrderTuple.loading && !didValidateWithServer && orderAsString) {
        validateOrder(orderAsString);
      }
    },
    [
      didValidateWithServer,
      validateCachedOrderTuple,
      validationErrors,
      orderAsString,
      validateOrder
    ]
  );

  const handleClickOverrideOrderForProcessing = React.useCallback(
    async () => {
      await overrideCachedOrder(JSON.parse(orderAsString));
      navigate(routeDirectory.admin.dataCache.path('invalid', queryTuple.data.storeId));
    },
    [
      navigate,
      overrideCachedOrder,
      orderAsString,
      queryTuple.data.storeId
    ]
  );



  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      const order = _.cloneDeep(JSON.parse(orderAsString) as object);
      _.forEach(formState.errors, err => {
        _.set(order, err.property, err.newValue);
      });
      setOrderAsString(JSON.stringify(order, null, 2));
    },
    [orderAsString]
  );


  const loading = queryTuple.loading || validateCachedOrderTuple.loading || overrideCachedOrderTuple.loading;
  return (
    <FormModal.FC
      isDoubleWide
      isLoading={loading}
      title="Edit Raw Order"
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={3}>
        <Grid
          item
          xs={6}>

          <List>
            {fieldArray.fields.map((err, index) => (
              <ListItem key={err.property}>
                <ControlledTextField
                  formControl={form.control}
                  label={err.property}
                  fieldKey={`errors.${index}.newValue`} />
              </ListItem>
            ))}
          </List>

          <Button
            variant="contained"
            type="submit">
            Save
          </Button>
        </Grid>

        <Grid
          item
          xs={6}>
          <Typography variant="h2">
            Validation errors
          </Typography>

          <List>
            {_.isEmpty(validationErrors) && (
              <ListItem>
                <ListItemIcon>
                  <Done color="success" />
                </ListItemIcon>

                <ListItemText
                  color="success"
                  secondary="Order is valid" />
              </ListItem>
            )}

            {_.map(validationErrors, (validationError, index) => (
              <ListItem key={index} >
                <ListItemIcon>
                  <Error color="error" />
                </ListItemIcon>

                <ListItemText
                  secondary={validationError.message}
                  color="error">
                </ListItemText>
              </ListItem>
            ))}

            {_.isEmpty(validationErrors) && (
              <ListItem>
                <Button
                  variant="contained"
                  color="success"
                  disabled={
                    !validateCachedOrderTuple.called ||
                    validateCachedOrderTuple.loading ||
                    overrideCachedOrderTuple.loading
                  }
                  onClick={handleClickOverrideOrderForProcessing}>
                  Override Order for Processing
                </Button>
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    </FormModal.FC>
  );
};
